import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Brand } from "@/components/AppLayout";

export default function InvitePage() {
  const { token } = useParams();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/team/invites/${token}/info`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/team/join", { token });
      setUser(data);
      toast.success(`You joined ${info.group_name}`);
      navigate("/");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const body = () => {
    if (error) return <p className="text-sm text-red-600" data-testid="invite-error">{error}</p>;
    if (!info || user === null) return <Loader2 className="h-6 w-6 mx-auto animate-spin text-primary" />;
    if (!user)
      return (
        <Button asChild className="w-full rounded-full h-11"><Link to={`/login?invite=${token}`} data-testid="invite-signin-link">Sign in or create account to join</Link></Button>
      );
    if (user.email !== info.email)
      return (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">This invite is for <b>{info.email}</b>, but you're signed in as {user.email}.</p>
          <Button variant="outline" className="w-full rounded-full" onClick={async () => { await logout(); navigate(`/login?invite=${token}`); }} data-testid="invite-switch-account">Switch account</Button>
        </div>
      );
    return (
      <Button className="w-full rounded-full h-11" onClick={join} disabled={busy} data-testid="invite-accept-button">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Join {info.group_name}
      </Button>
    );
  };

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-md rounded-2xl border bg-white p-10 text-center fade-up" data-testid="invite-page">
        <div className="flex justify-center mb-8"><Brand /></div>
        <div className="h-12 w-12 rounded-full bg-secondary text-primary grid place-items-center mx-auto"><Users className="h-6 w-6" /></div>
        <h1 className="mt-4 text-2xl font-bold">You're invited</h1>
        {info && <p className="mt-2 text-sm text-slate-500">{info.owner_name} invited you to join <b>{info.group_name}</b> on Beyond Boundaries ABA Scribe. Your notes stay private to you.</p>}
        <div className="mt-8">{body()}</div>
      </div>
    </div>
  );
}
